import type { ReflectoStorage } from "@reflecto/client-core";
import { useEffect, useState } from "react";
import { webStorage } from "../adapters/storage.web";

export interface UseStorageResult<V> {
  /** Latest value, or `undefined` if unset. Always `undefined` while `hydrated === false`. */
  value: V | undefined;
  /** True once the initial IndexedDB read has resolved. Use this to distinguish
   *  "not loaded yet" from "loaded and never written". */
  hydrated: boolean;
}

/**
 * Reactive hook that reads a single key from the web storage adapter and
 * re-renders whenever the value changes (in this tab, in another tab, or
 * from the service worker).
 *
 * Returns `{ value, hydrated }`. `value` is `undefined` until the initial
 * IndexedDB read completes. `hydrated` flips true exactly once per mount
 * when the read settles — use it to decide "has storage been read yet"
 * in cases where `value === undefined` is ambiguous (unset vs loading).
 *
 * Mirrors the extension's apps/extension/src/shared/hooks/useStorage.ts
 * API so shared UI components consume the same hook signature on either
 * platform.
 *
 * Ordering: the onChanged subscription is registered BEFORE the initial
 * `get` is dispatched, and a `seenChange` flag suppresses the late
 * `get` resolution if a change event already updated the value. This
 * closes the race where a write completes after the read is in flight
 * but before the listener is attached, which would otherwise leave the
 * hook stuck on a stale snapshot until the next write.
 *
 * If this hook ever lifts into packages/client-core/, take the
 * StorageAdapter via parameter or React context so it can be reused
 * by future Electron/Tauri shells without hardcoding `webStorage`.
 */
export function useStorage<K extends keyof ReflectoStorage>(
  key: K
): UseStorageResult<ReflectoStorage[K]> {
  const [value, setValue] = useState<ReflectoStorage[K] | undefined>(undefined);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let seenChange = false;

    // Reset on every effect run so a `key` prop change doesn't leave
    // the component rendering the previous key's value while the new
    // initial read is in flight.
    setValue(undefined);
    setHydrated(false);

    const unsubscribe = webStorage.onChanged((changes) => {
      if (key in changes) {
        seenChange = true;
        setValue(changes[key]?.newValue);
      }
    });

    webStorage
      .get(key)
      .then((result) => {
        if (cancelled) return;
        if (!seenChange) setValue(result[key]);
        // Flip hydrated AFTER value settles — a single render then
        // observes both at once and can't render a stale undefined.
        setHydrated(true);
      })
      .catch((err) => {
        console.error("useStorage: initial read failed", err);
        // Flip true anyway — staying stuck on the loader is worse
        // than letting the UI render from whatever value exists.
        if (!cancelled) setHydrated(true);
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [key]);

  return { value, hydrated };
}
